import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { api, ErreurApi, lire } from '../api'
import { type FichierVu, Visionneuse } from './ApercuPiece'
import { formaterInstant } from './Historique'

// Les échanges autour d'une pièce : le fil Gmail du mail qui l'a apportée (avant et après),
// lu à la demande dans Gmail, jamais gardé. Texte seul ; pièces jointes vues dans la
// visionneuse ou enregistrées. Chargé seulement quand on l'ouvre (un appel à Gmail).

const taille = (octets: number) =>
  octets >= 1_000_000
    ? `${(octets / 1_000_000).toFixed(1).replace('.', ',')} Mo`
    : `${Math.max(1, Math.round(octets / 1000))} ko`

function Fils({ pieceId }: { pieceId: string }) {
  const fils = useQuery({
    queryKey: ['pieces', 'mails', pieceId],
    queryFn: () => lire(api.pieces[':id'].mails.$get({ param: { id: pieceId } })),
    retry: false,
    staleTime: 5 * 60_000,
  })
  const [vu, setVu] = useState<FichierVu | null>(null)
  if (fils.isPending) return <p className="discret">Lecture des échanges…</p>
  if (fils.isError)
    return (
      <p className="discret">
        {fils.error instanceof ErreurApi ? fils.error.message : 'Échanges illisibles ici.'}
      </p>
    )
  if (fils.data.length === 0)
    return <p className="discret">Aucun mail lisible ici pour cette pièce.</p>
  const url = (message: string, partie: string) =>
    `/api/pieces/${pieceId}/mails/${message}/${partie}`
  return (
    <>
      {fils.data.map((f, i) =>
        'erreur' in f ? (
          <p key={i} className="discret">
            {f.boite} : {f.erreur}
          </p>
        ) : (
          <div key={i} className="fil-mails">
            <div className="discret">Boîte {f.boite}</div>
            {f.messages.map((m, j) => (
              <details key={m.message_id ?? j} className="mail" open={j === f.messages.length - 1}>
                <summary>
                  <strong>{m.objet ?? 'Mail sans objet'}</strong>
                  <span className="discret">
                    {' · '}
                    {[m.de, `le ${formaterInstant(m.recuLe)}`].filter(Boolean).join(' · ')}
                  </span>
                </summary>
                <div className="mail-texte">{m.texte ?? 'Mail sans texte.'}</div>
                {m.message_id && m.piecesJointes.length > 0 && (
                  <ul className="mail-pieces-jointes" aria-label="Pièces jointes">
                    {m.piecesJointes.map((p) => (
                      <li key={p.partie}>
                        <button
                          type="button"
                          className="mail-piece-jointe"
                          onClick={() =>
                            setVu({
                              url: url(m.message_id!, p.partie),
                              nom: p.nom,
                              typeMime: p.typeMime,
                            })
                          }
                        >
                          <strong>{p.nom}</strong>
                          <span className="discret">{taille(p.taille)}</span>
                        </button>
                        <a
                          className="bouton-discret mail-enregistrer"
                          href={url(m.message_id!, p.partie)}
                          download={p.nom}
                          aria-label={`Enregistrer ${p.nom}`}
                        >
                          Enregistrer
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </details>
            ))}
          </div>
        ),
      )}
      {vu && <Visionneuse f={vu} onFermer={() => setVu(null)} />}
    </>
  )
}

/** Bouton, puis les fils : rien n'est lu dans Gmail tant qu'on ne les ouvre pas. */
export function FilsPiece({ pieceId }: { pieceId: string }) {
  const [ouvert, setOuvert] = useState(false)
  if (!ouvert)
    return (
      <button type="button" className="bouton-discret" onClick={() => setOuvert(true)}>
        Afficher les échanges
      </button>
    )
  return <Fils pieceId={pieceId} />
}
